import { verifyTypedData } from '@ethersproject/wallet';
import { _TypedDataEncoder } from '@ethersproject/hash';
import { arrayify, concat } from '@ethersproject/bytes';
import getProvider, { type ProviderOptions } from '../utils/provider';
import { call } from '../utils';
import type { SignaturePayload } from '.';
import type { StaticJsonRpcProvider } from '@ethersproject/providers';
import { AbiCoder } from '@ethersproject/abi';

const ERC6492_DETECTION_SUFFIX =
  '6492649264926492649264926492649264926492649264926492649264926492';

function isEqual(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

export function getHash(data: SignaturePayload): string {
  const { domain, types, message } = data;
  return _TypedDataEncoder.hash(domain, types, message);
}

export default async function verify(
  address: string,
  sig: string,
  data: SignaturePayload,
  network = '1',
  options: ProviderOptions = {}
): Promise<boolean> {
  const { domain, types, message } = data;

  try {
    const recoverAddress = verifyTypedData(domain, types, message, sig);
    if (isEqual(address, recoverAddress)) return true;
  } catch (e: any) {}

  const provider = getProvider(network, options);
  const hash = getHash(data);

  // Handle EIP-6492
  // https://eips.ethereum.org/EIPS/eip-6492
  //
  // We can actually replace verifyTypedData and verifyDefault with the following code,
  // but https://github.com/AmbireTech/signature-validator/blob/main/contracts/DeploylessUniversalSigValidator.sol
  // also can send an extra network request to the provider. (with verifyTypedData we don't send any extra request)
  //
  if (sig.endsWith(ERC6492_DETECTION_SUFFIX)) {
    try {
      return (
        '0x01' ===
        (await provider.call({
          data: concat([
            '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',
            new AbiCoder().encode(
              ['address', 'bytes32', 'bytes'],
              [address, arrayify(hash), sig]
            )
          ])
        }))
      );
    } catch (error) {
      return false;
    }
  }

  // Handle EIP-1271
  if (await verifyDefault(address, sig, hash, provider)) return true;
  return await verifyOldVersion(address, sig, hash, provider);
}

async function verifyDefault(
  address: string,
  sig: string,
  hash: string,
  provider: StaticJsonRpcProvider
): Promise<boolean> {
  let returnValue: string;
  const magicValue = '0x1626ba7e';
  const abi =
    'function isValidSignature(bytes32 _hash, bytes memory _signature) public view returns (bytes4 magicValue)';
  try {
    returnValue = await call(
      provider,
      [abi],
      [address, 'isValidSignature', [arrayify(hash), sig]]
    );
  } catch (e: any) {
    if (e.message.startsWith('missing revert data in call exception')) {
      return false;
    }
    throw e;
  }

  return isEqual(returnValue, magicValue);
}

async function verifyOldVersion(
  address: string,
  sig: string,
  hash: string,
  provider: StaticJsonRpcProvider
): Promise<boolean> {
  const magicValue = '0x20c13b0b';
  const abi =
    'function isValidSignature(bytes _hash, bytes memory _signature) public view returns (bytes4 magicValue)';

  const returnValue = await call(
    provider,
    [abi],
    [address, 'isValidSignature', [arrayify(hash), sig]]
  );
  return isEqual(returnValue, magicValue);
}
